import React from 'react';
import { Link } from 'react-router-dom';
import { Book, ArrowLeft, Shield } from 'lucide-react';

export const UserManual = () => {
    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-800 p-8 pb-20">
            <div className="max-w-4xl mx-auto bg-white p-12 rounded-3xl shadow-xl border-4 border-slate-100">

                <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-green-600 font-bold mb-8 transition-colors">
                    <ArrowLeft size={20} /> Back to Dashboard
                </Link>

                <div className="flex items-center gap-4 mb-8 pb-8 border-b border-slate-100">
                    <div className="w-16 h-16 bg-green-100 text-green-600 rounded-2xl flex items-center justify-center transform -rotate-3">
                        <Book size={32} />
                    </div>
                    <div>
                        <h1 className="text-4xl font-black font-comic text-slate-900">Gorilla Scouts Field Manual</h1>
                        <p className="text-lg text-slate-500 font-medium">Standard Operating Procedures</p>
                    </div>
                </div>

                <div className="prose prose-slate max-w-none prose-headings:font-comic prose-h2:text-green-700 prose-a:text-blue-600">

                    <h2>1. The Dashboard (Home Base)</h2>
                    <p>Your central hub for all missions. Here you can track your progress and access your training modules.</p>
                    <ul>
                        <li><strong>Mission Log:</strong> Scroll through the list of available courses. Click on any course card to expand it and reveal the individual learning modules.</li>
                        <li><strong>Progress Compass:</strong> The bar at the top represents your current Experience Points (XP). Fill it up to level up!</li>
                        <li><strong>Service Record:</strong> Click your avatar in the top right to view your profile, earned badges, and printable certificates.</li>
                    </ul>

                    <h2>2. Completing Missions</h2>
                    <p>Each module is a self-contained lesson. Some are reading-focused, while others require you to write code, configure databases, or prompt AI.</p>
                    <ul>
                        <li><strong>Locked Modules:</strong> You must complete prerequisite modules (or earn badges) to unlock advanced topics.</li>
                        <li><strong>Interactive Labs:</strong> Look for the "Prompt Lab" or "Code Editor" logic within modules to practice hands-on.</li>
                    </ul>

                    <h2>3. The Prompt Lab (AI Training)</h2>
                    <p>Located in the <strong>AI Operator</strong> course, this simulator teaches you how to control Large Language Models.</p>
                    <ul>
                        <li>Use the **Role**, **Task**, **Context**, and **Format** controls to build the perfect prompt.</li>
                        <li>The simulator provides real-time feedback on your prompt's quality.</li>
                    </ul>

                    <h2>4. Troubleshooting</h2>
                    <div className="bg-amber-50 p-6 rounded-xl border-l-4 border-amber-400 my-6">
                        <h3 className="text-amber-800 font-bold mt-0">Help! I can't scroll!</h3>
                        <p className="text-amber-700 mb-0">Ensure your mouse cursor isn't stuck inside a code editor or a specific scrollable region. The main dashboard should scroll naturally.</p>
                    </div>

                    <h2>5. Parent's Guide (Safety & Technology)</h2>
                    <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                            <Shield className="text-green-600" />
                            <h3 className="text-green-800 font-bold m-0 text-lg">Is this "Real" AI?</h3>
                        </div>
                        <p className="text-green-700 font-bold mb-2">Short Answer: No. This is a Safe Simulation. 🛡️</p>
                        <ul className="list-disc pl-5 text-green-700 space-y-1 mb-0">
                            <li><strong>Offline Simulation:</strong> To ensure a safe, predictable, and 100% kid-friendly environment, this app mimics AI behavior using standard code. It does NOT connect to ChatGPT, Gemini, or the open internet.</li>
                            <li><strong>Zero Privacy Risk:</strong> We do not send any user input to external servers. No data leaves this computer.</li>
                            <li><strong>Controlled Content:</strong> Since the "AI" responses are pre-programmed or generated by safe logic, there is zero risk of the AI saying anything inappropriate or hallucinating false facts.</li>
                        </ul>
                    </div>
                </div>

                <div className="mt-12 pt-8 border-t border-slate-100 text-center text-slate-400 font-mono text-sm">
                    Gorilla Corp. Academy • Field Manual v1.0
                </div>

            </div>
        </div>
    );
};
